import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote, MapPin, Zap, Home } from "lucide-react";

const testimonials = [
  {
    name: "Marcos Oliveira",
    city: "Belo Horizonte, MG",
    role: "Empresário",
    service: "gd",
    serviceLabel: "Energia por Assinatura",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80",
    rating: 5,
    text: "Minha conta de luz era de R$ 1.200 por mês. Hoje pago R$ 900, sem ter feito nenhuma obra. A Solisy realmente entrega o que promete. Já indiquei para vários amigos!",
    savings: "R$ 300/mês de economia",
  },
  {
    name: "Patricia Mendes",
    city: "Uberlândia, MG",
    role: "Dona de casa",
    service: "install",
    serviceLabel: "Painéis Solares",
    photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=150&h=150&q=80",
    rating: 5,
    text: "Instalamos os painéis e nossa conta de luz foi praticamente a zero! A parcela do financiamento é quase o mesmo que pagávamos de luz, só que agora o sistema é nosso. Melhor investimento que fizemos.",
    savings: "Conta de luz zerada",
  },
  {
    name: "Ana Carolina Santos",
    city: "São Paulo, SP",
    role: "Dentista",
    service: "gd",
    serviceLabel: "Energia por Assinatura",
    photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150&q=80",
    rating: 5,
    text: "No começo fiquei desconfiada, mas depois de 6 meses posso confirmar: todo mês vem o desconto certinho na conta. O processo foi todo digital e super rápido.",
    savings: "R$ 180/mês de economia",
  },
  {
    name: "Roberto Almeida",
    city: "Contagem, MG",
    role: "Aposentado",
    service: "install",
    serviceLabel: "Painéis Solares",
    photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150&q=80",
    rating: 5,
    text: "Sou aposentado e fiz o financiamento dos painéis. A parcela ficou no mesmo valor da minha conta antiga, mas em 5 anos acaba e vou ter energia de graça pro resto da vida. Ainda vendo o excedente!",
    savings: "Energia grátis em 5 anos",
  },
  {
    name: "Juliana Ferreira",
    city: "Betim, MG",
    role: "Comerciante",
    service: "gd",
    serviceLabel: "Energia por Assinatura",
    photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=150&h=150&q=80",
    rating: 5,
    text: "Tenho uma loja e a conta de energia sempre foi uma dor de cabeça. Depois que assinei a Solisy, meu custo fixo reduziu bastante. O atendimento pelo WhatsApp é excelente!",
    savings: "R$ 450/mês de economia",
  },
  {
    name: "Carlos Eduardo Lima",
    city: "Sete Lagoas, MG",
    role: "Professor",
    service: "install",
    serviceLabel: "Painéis Solares",
    photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=150&h=150&q=80",
    rating: 5,
    text: "A equipe da Solisy foi super profissional na instalação. Em dois dias tava tudo pronto e funcionando. Minha conta veio R$ 30 esse mês. Antes era R$ 400!",
    savings: "De R$400 para R$30",
  },
];

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${star <= rating ? "star-filled fill-current" : "text-slate-200"}`}
      />
    ))}
  </div>
);

export const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [autoPlay]);

  const goTo = (idx) => {
    setAutoPlay(false);
    setCurrent(idx);
    setTimeout(() => setAutoPlay(true), 12000);
  };

  const prev = () => goTo((current - 1 + testimonials.length) % testimonials.length);
  const next = () => goTo((current + 1) % testimonials.length);

  const t = testimonials[current];

  return (
    <section
      id="depoimentos"
      data-testid="testimonials-section"
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: "#0c1b3a" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#f5b731]/15 text-[#f5b731] mb-4">
            Depoimentos
          </span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
            O que nossos clientes <span className="text-[#f5b731]">dizem</span>
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-xl mx-auto">
            Clientes satisfeitos com ambos os serviços. Resultados reais.
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="max-w-3xl mx-auto relative">
          {/* Navigation arrows */}
          <button
            data-testid="testimonial-prev"
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-14 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="testimonial-next"
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-14 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors duration-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
            >
              <div
                data-testid={`testimonial-card-${current}`}
                className="bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-10 relative"
              >
                <Quote className="w-10 h-10 mb-4 text-[#f5b731] opacity-30" />

                {/* Service badge */}
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-5 ${
                  t.service === "gd"
                    ? "bg-[#1a4fa0]/20 text-[#7eb3ff]"
                    : "bg-[#f5b731]/15 text-[#f5b731]"
                }`}>
                  {t.service === "gd" ? <Zap className="w-3 h-3" /> : <Home className="w-3 h-3" />}
                  {t.serviceLabel}
                </div>

                <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8">
                  "{t.text}"
                </p>

                {/* Savings badge */}
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-sm font-semibold mb-6">
                  {t.savings}
                </div>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <img
                    src={t.photo}
                    alt={t.name}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-[#f5b731]/30"
                  />
                  <div>
                    <p className="font-bold text-white">{t.name}</p>
                    <p className="text-sm text-white/50">{t.role}</p>
                    <div className="flex items-center gap-1 text-xs text-white/40 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {t.city}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <StarRating rating={t.rating} />
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((item, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => goTo(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === current ? "w-8 bg-[#f5b731]" : "w-2 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
